import React, { useState } from 'react';

import { Accordion, AccordionProps, ActionIcon, Alert, Flex, Title, useMantineTheme } from '@mantine/core';
import { IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ResourceConflictErrorModal } from 'components/ResourceConflictErrorModal';
import { DNSRecordTable } from 'pages/Settings/SettingTabs/WhiteLabelSettingsTab/DNSRecordTable';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { WhiteLabelService } from 'services/white-label-service';
import { WHITE_LABEL_DOMAINS_QUERY_KEY } from 'shared/constants/query-keys';
import { ROOT_PREFIX } from 'shared/constants/white-label-constant';
import { DeleteElementConflictDto } from 'shared/interfaces/General';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import {
  DeleteWhiteLabelSubDomainDto,
  WhiteLabelDomain,
  WhiteLabelSubDomain
} from 'shared/interfaces/WhiteLabelDomain';
import { buildDomainUrl, filterOutRootSubDomain } from 'shared/utils/domain';

interface WhiteLabelSubDomainsAccordionProps extends Omit<AccordionProps, 'children'> {
  domain: WhiteLabelDomain;
  openedSubDomainPrefix?: string;
  onToggleSubDomainPrefix?: (subDomainPrefix: string | null) => void;
}

export function WhiteLabelSubDomainsAccordion({
  domain,
  openedSubDomainPrefix,
  onToggleSubDomainPrefix
}: WhiteLabelSubDomainsAccordionProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();

  const subDomains = filterOutRootSubDomain(domain.subdomains);

  const [value, setValue] = useState<string | null>(openedSubDomainPrefix || subDomains.at(0)?.prefix || null);
  const [conflictedSmartAssistants, setConflictedSmartAssistants] = useState<SmartAssistant[]>([]);
  const [errorModalOpened, setErrorModalOpened] = useState(false);

  const SubDomainAccordionControl = ({ subDomain }: { subDomain: WhiteLabelSubDomain }) => {
    const handleSelectedSubDomainPostDeletion = (
      updatedDomain: WhiteLabelDomain,
      deleteWhiteLabelSubDomainDto: DeleteWhiteLabelSubDomainDto
    ) => {
      if (value !== deleteWhiteLabelSubDomainDto.prefix) return;

      const newSubDomains = filterOutRootSubDomain(updatedDomain.subdomains);
      const newOpenedSubDomainPrefix = newSubDomains.at(0)?.prefix || null;
      setValue(newOpenedSubDomainPrefix);
      onToggleSubDomainPrefix?.(newOpenedSubDomainPrefix);
    };

    const deleteSubDomainMutation = useMutation({
      mutationFn: WhiteLabelService.deleteWhiteLabelSubDomain,
      onSuccess: (updatedDomain: WhiteLabelDomain, deleteWhiteLabelSubDomainDto: DeleteWhiteLabelSubDomainDto) => {
        handleSelectedSubDomainPostDeletion(updatedDomain, deleteWhiteLabelSubDomainDto);
        queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) =>
          existingDomains.map((existingDomain) => (existingDomain._id === domain._id ? updatedDomain : existingDomain))
        );
        NotificationService.showResult(true, 'FEED_BACK.WHITE_LABEL.SUCCESS.DELETE_SUB_DOMAIN');
      },
      onError: (error: AxiosError) => {
        if (error.response?.status == HttpStatusCode.Conflict) {
          const data = error.response?.data as DeleteElementConflictDto;
          const conflictedSmartAssistants = data.smartAssistants;
          if (conflictedSmartAssistants && conflictedSmartAssistants.length > 0)
            setConflictedSmartAssistants(conflictedSmartAssistants);

          setErrorModalOpened(true);
        }
        NotificationService.showResult(false, 'FEED_BACK.WHITE_LABEL.FAILURE.DELETE_SUB_DOMAIN');
      }
    });

    return (
      <Flex direction="row" align="center" pl={0}>
        <Accordion.Control>
          <Title order={6}>{buildDomainUrl(subDomain.prefix, domain.value, false)}</Title>
        </Accordion.Control>
        {subDomain.prefix !== ROOT_PREFIX && (
          <ActionIcon
            loading={deleteSubDomainMutation.isLoading}
            onClick={(event) => {
              event?.stopPropagation();
              deleteSubDomainMutation.mutate({ domainId: domain._id, prefix: subDomain.prefix });
            }}
            color="red"
            mr="sm">
            <IconTrash />
          </ActionIcon>
        )}
      </Flex>
    );
  };

  return (
    <>
      <Accordion
        chevronPosition="left"
        value={value}
        onChange={(value) => {
          setValue(value);
          if (value) onToggleSubDomainPrefix?.(value);
        }}
        defaultValue={subDomains.at(0)?.prefix}
        styles={{
          item: {
            border: `1px solid ${theme.colors.gray[2]}`,
            marginTop: theme.spacing.md,
            borderRadius: theme.radius.md
          },
          panel: {
            padding: '0px'
          },
          control: {
            padding: theme.spacing.sm,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          },
          label: {
            padding: 0
          },
          content: {
            padding: theme.spacing.sm,
            paddingTop: '0px'
          }
        }}>
        {subDomains
          .sort((a, b) => a.prefix.localeCompare(b.prefix))
          .map((subDomain: WhiteLabelSubDomain, index: number) => (
            <Accordion.Item value={subDomain.prefix} key={index}>
              <SubDomainAccordionControl subDomain={subDomain} />
              <Accordion.Panel>
                <DNSRecordTable
                  record={subDomain.record}
                  alert={
                    <Alert
                      p="xs"
                      icon={<IconInfoCircle />}
                      title={t('WHITE_LABEL.RECORDS.INFO', {
                        recordType: subDomain.record.type,
                        subDomain: buildDomainUrl(subDomain.prefix, domain.value, false)
                      })}
                      color="blue"
                      styles={{
                        root: { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                        title: { marginBottom: 0, fontWeight: 500, marginTop: 2.5 }
                      }}>
                      {null}
                    </Alert>
                  }
                />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
      </Accordion>
      <ResourceConflictErrorModal
        errorModalOpened={errorModalOpened}
        conflictedSmartAssistants={conflictedSmartAssistants}
        onClose={() => setErrorModalOpened(false)}
      />
    </>
  );
}
